"use client";

import { useRouter } from "next/navigation";
import { createContext, type ReactNode, use, useOptimistic, useRef } from "react";
import { cartSetQuantityAction } from "@/actions/cart";
import type { CartGetResult } from "@/dao/cart";
import type { ProductBrowseResult } from "@/dao/product";
import { assertUnreachable, invariant } from "@/lib/utils";

interface CartContextValues {
	cart: CartGetResult | undefined | null;
	dispatch: (action: Actions) => void;
}

const CartContextProvider = createContext<null | CartContextValues>(null);

type Actions =
	| { type: "INCREASE" | "DECREASE"; lineId: string }
	| {
			type: "ADD_ITEM";
			product: ProductBrowseResult["data"][number];
			variant: ProductBrowseResult["data"][number]["variants"][number];
	  };

export const CartContext = ({ children, cart }: { children: ReactNode; cart: CartContextValues["cart"] }) => {
	const [optimisticCart, dispatchCartAction] = useOptimistic(cart, (state, action: Actions) => {
		if (!state) {
			return state;
		}

		switch (action.type) {
			case "INCREASE":
				return state?.lineItems.find((line) => line.id === action.lineId)?.quantity
					? {
							...state,
							lineItems: state.lineItems.map((line) => {
								if (line.id === action.lineId) {
									return { ...line, quantity: line.quantity + 1 };
								}
								return line;
							}),
						}
					: state;
			case "DECREASE":
				return state?.lineItems.find((line) => line.id === action.lineId)?.quantity
					? {
							...state,
							lineItems: state.lineItems
								.map((line) => {
									if (line.id === action.lineId) {
										if (line.quantity - 1 <= 0) {
											return null;
										}
										return { ...line, quantity: line.quantity - 1 };
									}
									return line;
								})
								.filter(Boolean),
						}
					: state;
			case "ADD_ITEM":
				return state?.lineItems.find((line) => line.productVariantId === action.variant.id)
					? {
							...state,
							lineItems: state.lineItems.map((line) => {
								if (line.productVariantId === action.variant.id) {
									return { ...line, quantity: line.quantity + 1 };
								}
								return line;
							}),
						}
					: {
							...state,
							lineItems: state.lineItems.concat({
								id: global.crypto.randomUUID(),
								createdAt: new Date().toISOString(),
								updatedAt: new Date().toISOString(),
								cartId: state.id,
								productVariantId: action.variant.id,
								quantity: 1,
								productVariant: { ...action.variant, product: action.product },
							}),
						};
		}
		assertUnreachable(action);
		return state;
	});

	return (
		<CartContextProvider.Provider value={{ cart: optimisticCart, dispatch: dispatchCartAction }}>
			{children}
		</CartContextProvider.Provider>
	);
};

export const useCart = () => {
	const value = use(CartContextProvider);
	invariant(value, "`useCart` must be used within a `CartContext`");
	return value;
};

export const useChangeQuantity = (quantity: number) => {
	const router = useRouter();
	const stateRef = useRef<{
		timer: ReturnType<typeof setTimeout> | null;
		promise: PromiseWithResolvers<void>;
	}>(null);
	const formAction = async ({ type, lineId }: { type: "INCREASE" | "DECREASE"; lineId: string }) => {
		const doWork = async () => {
			try {
				const modifier = type === "INCREASE" ? 1 : -1;
				const newQuantity = quantity + modifier;
				await cartSetQuantityAction({
					lineId,
					quantity: newQuantity,
				});
				router.refresh();
				stateRef.current?.promise.resolve();
			} catch (error) {
				stateRef.current?.promise.reject(error);
			} finally {
				stateRef.current = null;
			}
		};

		if (stateRef.current) {
			clearTimeout(stateRef.current.timer ?? undefined);
			stateRef.current.timer = setTimeout(doWork, 400);
		} else {
			stateRef.current = {
				timer: setTimeout(doWork, 400),
				promise: Promise.withResolvers(),
			};
		}
		return stateRef.current.promise.promise;
	};

	return { formAction, isPending: !!stateRef.current };
};
